import React, { useEffect, useState } from "react";
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, Lock, ShieldCheck, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, imgUrl } from "@/lib/api";
import { useShop } from "@/lib/shop";

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")}€`;
const shopCopy = (shop, kind) => {
  if (!shop?.prices?.monthly) return null;
  const m = eur(shop.prices.monthly), d = eur(shop.prices.download);
  return { subscription: { title: `Abbonati a ${shop.name}`, price: m, note: "al mese · disdici quando vuoi", cta: `Abbonati ora · ${m}/mese` },
    promo: { title: "Offerta speciale: 7 giorni", price: "2,99€", note: `7 giorni di accesso completo, poi ${m}/mese · disdici quando vuoi`, cta: "Attiva a 2,99€" },
    course: { title: "Acquista questo corso", price: d, note: "pagamento una tantum · tuo per sempre, anche in PDF", cta: `Acquista a ${d}` } }[kind];
};
import { getStripe, STRIPE_APPEARANCE } from "@/lib/stripe";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const COPY = {
  subscription: { title: "Attiva Corsando Premium", price: "11,99€", note: "al mese · disdici quando vuoi", cta: "Abbonati ora · 11,99€/mese" },
  promo: { title: "Offerta speciale: 7 giorni", price: "2,99€", note: "7 giorni di accesso completo, poi 11,99€/mese · disdici quando vuoi", cta: "Attiva a 2,99€" },
  download: { title: "Download aggiuntivo PDF", price: "9,99€", note: "pagamento una tantum", cta: "Paga 9,99€ e scarica" },
  setup: { title: "Aggiorna metodo di pagamento", price: null, note: "Nessun addebito ora: useremo questa carta per i prossimi rinnovi.", cta: "Salva metodo di pagamento" },
};

const PayForm = ({ intent, kind, onDone, confirmPath = "/payments/confirm", cta, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [wallets, setWallets] = useState(false);
  const isSetup = intent.mode === "setup";

  const finish = async () => {
    for (let i = 0; i < 8; i++) {
      try {
        const { data } = await api.post(confirmPath, { ref: intent.ref });
        if (data.payment_status === "paid") { window.dispatchEvent(new Event("corsando:payment-success")); return onDone(); }
      } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); break; }
      await new Promise((r) => setTimeout(r, 1500));
    }
    setBusy(false);
    setErr("Pagamento in verifica. Se l'addebito è andato a buon fine si attiverà a breve.");
  };

  const confirm = async (validate) => {
    if (!stripe || !elements) return;
    setBusy(true); setErr("");
    if (validate) {
      const { error } = await elements.submit();
      if (error) { setErr(error.message); setBusy(false); return; }
    }
    const fn = isSetup ? stripe.confirmSetup : stripe.confirmPayment;
    const { error } = await fn({
      elements, clientSecret: intent.client_secret, redirect: "if_required",
      confirmParams: { return_url: `${window.location.origin}/payment/success?ref=${intent.ref}` },
    });
    if (error) { setErr(error.message); setBusy(false); onError?.(error.message); return; }
    await finish();
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); confirm(true); }} data-testid="payment-form">
      <div className={wallets ? "mb-4" : "hidden"} data-testid="wallet-buttons">
        <ExpressCheckoutElement
          onReady={({ availablePaymentMethods }) => setWallets(!!availablePaymentMethods)}
          onConfirm={() => confirm(false)}
          options={{
            paymentMethods: { googlePay: "always", applePay: "always", link: "never", paypal: "never", amazonPay: "never", klarna: "never" },
            buttonHeight: 48,
          }} />
        <div className="flex items-center gap-3 my-4 text-xs text-slate-500">
          <div className="flex-1 h-px bg-white/10" /> oppure con carta <div className="flex-1 h-px bg-white/10" />
        </div>
      </div>
      <PaymentElement options={{ layout: "tabs", wallets: { applePay: "never", googlePay: "never" }, defaultValues: { billingDetails: { address: { country: "IT" } } } }} />
      {err && <p className="text-rose-400 text-sm mt-3" data-testid="payment-error">{err}</p>}
      <button type="submit" disabled={busy || !stripe} data-testid="payment-submit-btn"
        className="mt-5 w-full h-13 py-3.5 rounded-xl bg-[var(--cs-red)] hover:bg-[var(--cs-red-hover)] text-white font-bold text-base transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        {busy ? <><Loader2 className="animate-spin" size={18} /> Elaborazione...</> : <><Lock size={16} /> {cta}</>}
      </button>
      <p className="mt-3 text-[11px] text-slate-500 flex items-center justify-center gap-1.5">
        <ShieldCheck size={13} /> Pagamento crittografato · Carta, Google Pay, Apple Pay
      </p>
    </form>
  );
};

export const PaymentDialog = ({ open, kind = "subscription", promo, courseId, onClose, onSuccess, fetchIntent, confirmPath, copy, onError, variant }) => {
  const light = variant === "voli";
  const [intent, setIntent] = useState(null);
  const shop = useShop();

  useEffect(() => {
    if (!open) { setIntent(null); return; }
    (fetchIntent ? fetchIntent() : api.post("/payments/intent", { kind, promo, course_id: courseId }))
      .then(({ data }) => setIntent(data))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); onClose?.(); });
    // eslint-disable-next-line
  }, [open, kind]);

  const c = copy || shopCopy(shop, kind) || COPY[kind] || COPY.subscription;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className={`max-w-md max-h-[94vh] overflow-y-auto p-0 ${light ? "bg-white border border-slate-200 text-slate-900 pay-voli" : "bg-[#0f0f16] border border-white/10 text-white"}`} data-testid="payment-dialog">
        <div className="relative overflow-hidden px-6 pt-6 pb-5 border-b border-white/10"
          style={{ background: light ? "#0B3D91" : "linear-gradient(120deg, rgba(229,9,20,.28), transparent 65%)", color: light ? "#fff" : undefined }}>
          {copy?.brand ? <p className="font-bold mb-4">{copy.brand}</p> : shop ? (shop.logo ? <img src={imgUrl(shop.logo)} alt={shop.name} className="h-6 w-auto mb-4" /> : <p className="font-bold mb-4">{shop.name}</p>) : <img src="/corsando-logo.png" alt="Corsando" className="h-5 w-auto mb-4" />}
          <DialogHeader className="text-left space-y-1">
            <DialogTitle className="font-display text-xl font-extrabold text-white">{c.title}</DialogTitle>
            <DialogDescription className={light ? "text-blue-100 text-sm" : "text-slate-400 text-sm"}>{c.note}</DialogDescription>
          </DialogHeader>
          {c.price && (
            <div className="mt-3 flex items-end gap-1" data-testid="payment-amount">
              <span className="font-display text-4xl font-extrabold">{c.price}</span>
              {(c.monthly || (!copy && kind === "subscription")) && <span className="text-slate-400 mb-1">/mese</span>}
            </div>
          )}
        </div>
        <div className="px-6 py-5">
          {intent ? (
            <Elements stripe={getStripe()} options={{ clientSecret: intent.client_secret, appearance: light ? { theme: "stripe", variables: { colorPrimary: "#0B3D91", borderRadius: "8px" } } : STRIPE_APPEARANCE, locale: "it" }}>
              <PayForm intent={intent} kind={kind} confirmPath={confirmPath} cta={c.cta} onError={onError} onDone={() => { onSuccess?.(); onClose?.(); }} />
            </Elements>
          ) : (
            <div className="py-14 flex flex-col items-center text-slate-400 gap-2" data-testid="payment-loading">
              <CreditCard className="animate-pulse" size={28} /> Preparazione pagamento sicuro...
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
